import React, { useState, useRef } from "react";
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Alert,
    MenuItem,
    Chip,
    LinearProgress,
    IconButton,
    Tooltip,
    useTheme,
    alpha,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import WorkIcon from "@mui/icons-material/Work";
import CodeIcon from "@mui/icons-material/Code";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScienceIcon from "@mui/icons-material/Science";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";

// Logos
import SwanLogo from "../../assets/logos/swansolutions_logo.png";
import FigLogo from "../../assets/logos/fig_CROP.png";
import SwanAiLogo from "../../assets/logos/swan_ai_labs_logo.png";

/* ─────────────────────────────────────────────────────────────
   GLASSMORPHISM STYLE HELPERS
───────────────────────────────────────────────────────────── */
const glassCard = (theme, intensity = 0.12) => ({
    background: alpha(theme.palette.common.white, intensity),
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
    borderRadius: "24px",
    boxShadow: `
        0 8px 32px ${alpha(theme.palette.primary.main, 0.08)},
        inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.1)}
    `,
});

const glassDark = (theme) => ({
    background: alpha(theme.palette.primary.dark, 0.6),
    backdropFilter: "blur(24px) saturate(200%)",
    WebkitBackdropFilter: "blur(24px) saturate(200%)",
    border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
    borderRadius: "24px",
    boxShadow: `0 12px 48px ${alpha(theme.palette.common.black, 0.25)}`,
});

/* ─────────────────────────────────────────────────────────────
   ANIMATION VARIANTS
───────────────────────────────────────────────────────────── */
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.12, delayChildren: 0.1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { type: "spring", stiffness: 100, damping: 15 },
    },
};

const hapticTap = {
    scale: 0.97,
    transition: { type: "spring", stiffness: 400, damping: 17 },
};

const hapticHover = {
    scale: 1.02,
    y: -4,
    transition: { type: "spring", stiffness: 300, damping: 20 },
};

const glowPulse = {
    boxShadow: [
        "0 0 20px rgba(33, 219, 184, 0.3)",
        "0 0 40px rgba(33, 219, 184, 0.5)",
        "0 0 20px rgba(33, 219, 184, 0.3)",
    ],
    transition: { duration: 2, repeat: Infinity, ease: "easeInOut" },
};

/* ─────────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────────── */
const careerPaths = [
    {
        type: "Sales & Marketing",
        description:
            "Drive growth and connect K12 schools with Swan Solutions' transformative technology. Build lasting relationships and explore new markets.",
        icon: FigLogo,
        position: "Sales/Marketing",
        gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        muiIcon: TrendingUpIcon,
        tags: ["Growth", "Partnerships", "Education"],
    },
    {
        type: "Full Stack Development",
        description:
            "Build scalable, secure, and elegant solutions that power K12 education nationwide. Clean code, modern architectures, real impact.",
        icon: SwanAiLogo,
        position: "Full Stack Developer",
        gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
        muiIcon: CodeIcon,
        tags: ["React", "Node.js", "Cloud"],
    },
    {
        type: "Data Science",
        description:
            "Transform educational data into actionable insights. Machine learning, analytics, and visualization that drives student success.",
        icon: SwanAiLogo,
        position: "Data Scientist",
        gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
        muiIcon: ScienceIcon,
        tags: ["ML", "Analytics", "Python"],
    },
    {
        type: "AI Development",
        description:
            "Pioneer the future of AI in education. Build intelligent systems that personalize learning and empower educators.",
        icon: SwanAiLogo,
        position: "AI Developer",
        gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
        muiIcon: AutoAwesomeIcon,
        tags: ["LLMs", "NLP", "Innovation"],
    },
];

const positionOptions = [
    { value: "Sales/Marketing", label: "Sales & Marketing" },
    { value: "Full Stack Developer", label: "Full Stack Developer" },
    { value: "Data Scientist", label: "Data Scientist" },
    { value: "AI Developer", label: "AI Developer" },
];

const initialForm = {
    FirstName: "",
    LastName: "",
    Email: "",
    Phone: "",
    AboutMe: "",
    Position: "",
    Resume: null,
};

/* ─────────────────────────────────────────────────────────────
   FLOATING PARTICLE BACKGROUND
───────────────────────────────────────────────────────────── */
const FloatingParticles = () => {
    const particles = Array.from({ length: 20 }, (_, i) => ({
        id: i,
        size: Math.random() * 6 + 2,
        x: Math.random() * 100,
        y: Math.random() * 100,
        duration: Math.random() * 20 + 15,
        delay: Math.random() * 5,
    }));

    return (
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                overflow: "hidden",
                pointerEvents: "none",
                zIndex: 0,
            }}
        >
            {particles.map((p) => (
                <motion.div
                    key={p.id}
                    initial={{ x: `${p.x}vw`, y: `${p.y}vh`, opacity: 0 }}
                    animate={{
                        y: [`${p.y}vh`, `${p.y - 30}vh`, `${p.y}vh`],
                        opacity: [0, 0.6, 0],
                    }}
                    transition={{
                        duration: p.duration,
                        repeat: Infinity,
                        delay: p.delay,
                        ease: "easeInOut",
                    }}
                    style={{
                        position: "absolute",
                        width: p.size,
                        height: p.size,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.4)",
                    }}
                />
            ))}
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────
   CAREER CARD COMPONENT
───────────────────────────────────────────────────────────── */
const CareerCard = ({ path, index, onSelect }) => {
    const theme = useTheme();
    const IconComponent = path.muiIcon;

    return (
        <motion.div
            variants={itemVariants}
            whileHover={hapticHover}
            whileTap={hapticTap}
            style={{ height: "100%" }}
        >
            <Card
                onClick={() => onSelect(path.position)}
                sx={{
                    ...glassCard(theme, 0.85),
                    height: "100%",
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
                        "& .career-icon-bg": {
                            transform: "scale(1.1) rotate(5deg)",
                        },
                        "& .career-gradient-bar": {
                            height: "6px",
                        },
                    },
                }}
            >
                {/* Gradient top bar */}
                <Box
                    className="career-gradient-bar"
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: path.gradient,
                        transition: "height 0.3s ease",
                    }}
                />

                <CardContent sx={{ p: 3.5, pt: 4 }}>
                    {/* Icon container */}
                    <Box
                        className="career-icon-bg"
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: "20px",
                            background: path.gradient,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2.5,
                            transition: "transform 0.3s ease",
                            boxShadow: `0 8px 24px ${alpha("#000", 0.15)}`,
                        }}
                    >
                        <IconComponent sx={{ fontSize: 36, color: "#fff" }} />
                    </Box>

                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 800,
                            mb: 1.5,
                            color: theme.palette.primary.dark,
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {path.type}
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: theme.palette.text.secondary,
                            mb: 2.5,
                            lineHeight: 1.7,
                        }}
                    >
                        {path.description}
                    </Typography>

                    {/* Tags */}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {path.tags.map((tag) => (
                            <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                sx={{
                                    background: alpha(theme.palette.primary.main, 0.08),
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    borderRadius: "8px",
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                }}
                            />
                        ))}
                    </Box>
                </CardContent>
            </Card>
        </motion.div>
    );
};

/* ─────────────────────────────────────────────────────────────
   FILE UPLOAD DROPZONE
───────────────────────────────────────────────────────────── */
const FileDropzone = ({ file, onChange, onClear }) => {
    const theme = useTheme();
    const [isDragging, setIsDragging] = useState(false);
    const inputRef = useRef(null);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragIn = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragOut = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            onChange({ target: { name: "Resume", files: e.dataTransfer.files } });
        }
    };

    return (
        <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
            <Box
                onDragEnter={handleDragIn}
                onDragLeave={handleDragOut}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => !file && inputRef.current?.click()}
                sx={{
                    position: "relative",
                    border: `2px dashed ${isDragging
                        ? theme.palette.secondary.main
                        : alpha(theme.palette.primary.main, 0.3)
                        }`,
                    borderRadius: "16px",
                    p: 4,
                    textAlign: "center",
                    cursor: file ? "default" : "pointer",
                    background: isDragging
                        ? alpha(theme.palette.secondary.main, 0.08)
                        : alpha(theme.palette.primary.main, 0.02),
                    transition: "all 0.3s ease",
                    "&:hover": !file && {
                        borderColor: theme.palette.secondary.main,
                        background: alpha(theme.palette.secondary.main, 0.05),
                    },
                }}
            >
                <input
                    ref={inputRef}
                    name="Resume"
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.rtf"
                    onChange={onChange}
                    hidden
                />

                <AnimatePresence mode="wait">
                    {file ? (
                        <motion.div
                            key="file"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: "12px",
                                        background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <AttachFileIcon sx={{ color: "#fff" }} />
                                </Box>
                                <Box sx={{ textAlign: "left" }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                                        {file.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                        {(file.size / 1024).toFixed(1)} KB
                                    </Typography>
                                </Box>
                                <Tooltip title="Remove file">
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onClear();
                                        }}
                                        size="small"
                                        sx={{
                                            ml: 1,
                                            color: theme.palette.error.main,
                                            "&:hover": { background: alpha(theme.palette.error.main, 0.1) },
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="upload"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <CloudUploadIcon
                                sx={{
                                    fontSize: 48,
                                    color: isDragging ? theme.palette.secondary.main : theme.palette.primary.light,
                                    mb: 1.5,
                                }}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                {isDragging ? "Drop your resume here" : "Drag & drop your resume"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                                or click to browse • PDF, DOC, DOCX accepted
                            </Typography>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>
        </motion.div>
    );
};

/* ─────────────────────────────────────────────────────────────
   MAIN CAREERS COMPONENT
───────────────────────────────────────────────────────────── */
export default function Careers() {
    const theme = useTheme();
    const [form, setForm] = useState(initialForm);
    const [status, setStatus] = useState({ loading: false, error: null, success: null });
    const formRef = useRef(null);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "Resume") {
            setForm({ ...form, Resume: files[0] || null });
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    const handleClearFile = () => {
        setForm({ ...form, Resume: null });
    };

    const handleSelectPosition = (position) => {
        setForm({ ...form, Position: position });
        // Smooth scroll to form
        setTimeout(() => {
            formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
    };

    const validate = () => {
        if (
            !form.FirstName.trim() ||
            !form.LastName.trim() ||
            !form.Email.trim() ||
            !form.Phone.trim() ||
            !form.Position.trim() ||
            !form.AboutMe.trim() ||
            !form.Resume
        )
            return false;
        if (!/\S+@\S+\.\S+/.test(form.Email)) return false;
        if (!/\d{7,}/.test(form.Phone)) return false;
        return true;
    };

    const buildHtml = (resumeUrl) => `
    <div style="font-family:Segoe UI,Arial,sans-serif;max-width:600px;margin:0 auto;">
      <h2 style="background:#21dbb8;padding:18px 0;color:#054067;text-align:center;margin-top:0;">
        New Career Application - Swan Solutions
      </h2>
      <table style="width:100%;font-size:1rem;border-collapse:collapse;">
        <tr><td style="font-weight:bold;padding:8px;">First Name:</td><td>${form.FirstName}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Last Name:</td><td>${form.LastName}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Email:</td><td>${form.Email}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Phone:</td><td>${form.Phone}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Position:</td><td>${form.Position}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">About Me:</td><td>${form.AboutMe.replace(/\n/g, "<br/>")}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Resume:</td><td><a href="${resumeUrl}" target="_blank">${resumeUrl}</a></td></tr>
      </table>
      <hr style="margin:30px 0 12px 0;">
      <div style="color:#7e8b99;font-size:0.96rem;">Career form submitted from <b>swansolutions.com</b></div>
    </div>
  `;

    const buildText = (resumeUrl) => `
New Career Application - Swan Solutions

First Name: ${form.FirstName}
Last Name: ${form.LastName}
Email: ${form.Email}
Phone: ${form.Phone}
Position: ${form.Position}
About Me:
${form.AboutMe}

Resume: ${resumeUrl}
  `;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, error: null, success: null });

        if (!validate()) {
            setStatus({
                loading: false,
                error: "Please fill all required fields and attach your resume.",
                success: null,
            });
            return;
        }

        // 1. Upload the resume file
        let resumeUrl = "";
        try {
            const fileData = new FormData();
            fileData.append("file", form.Resume, form.Resume.name);
            fileData.append("folder", "resume");

            const fileRes = await fetch("https://media.swancreate.com/upload", {
                method: "POST",
                body: fileData,
            });

            const fileJson = await fileRes.json();
            let uploadedFileName = "";
            if (fileJson && typeof fileJson.message === "string") {
                const match = fileJson.message.match(/'([^']+)'/);
                if (match && match[1]) {
                    uploadedFileName = match[1];
                }
            }

            if (uploadedFileName && fileRes.ok) {
                resumeUrl = `https://media.swancreate.com/images/resumes/${uploadedFileName}`;
            } else {
                throw new Error("File upload failed");
            }
        } catch (err) {
            setStatus({
                loading: false,
                error: "Resume upload failed. Please try again.",
                success: null,
            });
            return;
        }

        // 2. Post to the SQL API
        try {
            const dbRes = await fetch("https://figazuresql.swancreate.com/api/crm/CareerPage", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    FirstName: form.FirstName,
                    LastName: form.LastName,
                    Email: form.Email,
                    Phone: form.Phone,
                    AboutMe: form.AboutMe,
                    Position: form.Position,
                }),
            });
            if (!dbRes.ok) throw new Error("DB error");
        } catch {
            setStatus({
                loading: false,
                error: "Error saving your application. Please try again.",
                success: null,
            });
            return;
        }

        // 3. Send the notification email
        try {
            const emailRes = await fetch("https://mailgun.swancreate.com/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    to: "dusty@swank12.com,becki@figk-12.com",
                    subject: "New Career Application — Swan Solutions Website",
                    html: buildHtml(resumeUrl),
                    text: buildText(resumeUrl),
                }),
            });
            if (!emailRes.ok) throw new Error("Email error");
        } catch {
            setStatus({
                loading: false,
                error: "Unable to send email notification. Your application was received.",
                success: null,
            });
            return;
        }

        setStatus({
            loading: false,
            error: null,
            success: "Thank you! Your application has been submitted successfully.",
        });
        setForm(initialForm);
    };

    /* ─────────── GLASSMORPHIC INPUT STYLES ─────────── */
    const glassInputSx = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            background: alpha(theme.palette.common.white, 0.6),
            backdropFilter: "blur(8px)",
            transition: "all 0.3s ease",
            "& fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.2),
                borderWidth: "1.5px",
            },
            "&:hover fieldset": {
                borderColor: theme.palette.secondary.main,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.secondary.main,
                borderWidth: "2px",
            },
            "&.Mui-focused": {
                background: alpha(theme.palette.common.white, 0.9),
                boxShadow: `0 0 0 4px ${alpha(theme.palette.secondary.main, 0.15)}`,
            },
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: theme.palette.secondary.dark,
        },
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: `linear-gradient(180deg, 
                    ${theme.palette.background.default} 0%, 
                    ${alpha(theme.palette.secondary.light, 0.1)} 50%,
                    ${theme.palette.background.default} 100%)`,
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* ═══════════════════════════════════════════════════════════
                HERO SECTION
            ═══════════════════════════════════════════════════════════ */}
            <Box
                component={motion.section}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                sx={{
                    position: "relative",
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    py: { xs: 10, md: 14 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: `
                        linear-gradient(180deg, 
                            #4db8e8 0%,
                            #2a7ab8 25%,
                            ${theme.palette.primary.main} 50%,
                            ${theme.palette.primary.dark} 80%,
                            #0a2540 100%)
                    `,
                    minHeight: { xs: "45vh", md: "50vh" },
                    overflow: "hidden",
                }}
            >
                <FloatingParticles />

                {/* Logo */}
                <motion.div
                    initial={{ opacity: 0, y: -30, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
                >
                    <Box
                        component="img"
                        src={SwanLogo}
                        alt="Swan Solutions Logo"
                        sx={{
                            height: { xs: 80, md: 110 },
                            mb: 3,
                        }}
                    />
                </motion.div>

                {/* Title */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                >
                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            fontSize: { xs: "2.5rem", md: "3.5rem" },
                            letterSpacing: "-0.04em",
                            textShadow: "0 4px 24px rgba(0,0,0,0.3)",
                            mb: 2,
                        }}
                    >
                        Shape the Future of
                        <br />
                        <Box
                            component="span"
                            sx={{
                                background: "linear-gradient(90deg, #21dbb8 0%, #66EDFF 100%)",
                                backgroundClip: "text",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            K12 Education
                        </Box>
                    </Typography>
                </motion.div>

                {/* Subtitle */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            maxWidth: 700,
                            mx: "auto",
                            px: 3,
                            color: "rgba(255,255,255,0.9)",
                            fontWeight: 400,
                            lineHeight: 1.7,
                            textShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        }}
                    >
                        Join a team committed to continuous improvement, innovation, and making a real
                        impact in districts nationwide. Your expertise can help transform education technology.
                    </Typography>
                </motion.div>

                {/* Scroll indicator */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.5 }}
                    style={{ marginTop: 40 }}
                >
                    <motion.div
                        animate={{ y: [0, 10, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                    >
                        <WorkIcon sx={{ fontSize: 32, opacity: 0.7 }} />
                    </motion.div>
                </motion.div>
            </Box>

            {/* ═══════════════════════════════════════════════════════════
                CAREER PATHS
            ═══════════════════════════════════════════════════════════ */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            textAlign: "center",
                            mb: 2,
                            color: theme.palette.primary.dark,
                            letterSpacing: "-0.03em",
                        }}
                    >
                        Open Positions
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: "center",
                            color: theme.palette.text.secondary,
                            maxWidth: 600,
                            mx: "auto",
                            mb: 6,
                        }}
                    >
                        Click on a role to learn more and apply. We're always looking for exceptional
                        talent to join our growing team.
                    </Typography>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                                lg: "repeat(4, 1fr)",
                            },
                            gap: 3,
                        }}
                    >
                        {careerPaths.map((path, idx) => (
                            <CareerCard key={path.type} path={path} index={idx} onSelect={handleSelectPosition} />
                        ))}
                    </Box>
                </motion.div>
            </Container>

            {/* ═══════════════════════════════════════════════════════════
                APPLICATION FORM
            ═══════════════════════════════════════════════════════════ */}
            <Container maxWidth="md" sx={{ pb: 12 }}>
                <motion.div
                    ref={formRef}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7 }}
                >
                    <Card
                        sx={{
                            ...glassCard(theme, 0.92),
                            p: { xs: 3, md: 5 },
                            position: "relative",
                            overflow: "visible",
                        }}
                    >
                        {/* Decorative accent */}
                        <Box
                            sx={{
                                position: "absolute",
                                top: -3,
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: "60%",
                                height: 6,
                                borderRadius: "0 0 8px 8px",
                                background: `linear-gradient(90deg, 
                                    ${theme.palette.primary.main}, 
                                    ${theme.palette.secondary.main})`,
                            }}
                        />

                        {/* Loading bar */}
                        <AnimatePresence>
                            {status.loading && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        borderRadius: "24px 24px 0 0",
                                        overflow: "hidden",
                                    }}
                                >
                                    <LinearProgress
                                        sx={{
                                            height: 4,
                                            "& .MuiLinearProgress-bar": {
                                                background: `linear-gradient(90deg, 
                                                    ${theme.palette.secondary.main}, 
                                                    ${theme.palette.primary.main})`,
                                            },
                                        }}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 800,
                                textAlign: "center",
                                mb: 1,
                                color: theme.palette.primary.dark,
                            }}
                        >
                            Apply Now
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                textAlign: "center",
                                color: theme.palette.text.secondary,
                                mb: 4,
                            }}
                        >
                            Tell us about yourself and upload your resume
                        </Typography>

                        <form onSubmit={handleSubmit} autoComplete="off">
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                    gap: 3,
                                }}
                            >
                                {/* First Name */}
                                <motion.div whileFocus={{ scale: 1.01 }}>
                                    <TextField
                                        required
                                        name="FirstName"
                                        label="First Name"
                                        value={form.FirstName}
                                        onChange={handleChange}
                                        fullWidth
                                        sx={glassInputSx}
                                    />
                                </motion.div>

                                {/* Last Name */}
                                <TextField
                                    required
                                    name="LastName"
                                    label="Last Name"
                                    value={form.LastName}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={glassInputSx}
                                />

                                {/* Email */}
                                <TextField
                                    required
                                    name="Email"
                                    label="Email"
                                    type="email"
                                    value={form.Email}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={glassInputSx}
                                />

                                {/* Phone */}
                                <TextField
                                    required
                                    name="Phone"
                                    label="Phone"
                                    value={form.Phone}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={glassInputSx}
                                />

                                {/* Position */}
                                <Box sx={{ gridColumn: { sm: "span 2" } }}>
                                    <TextField
                                        select
                                        required
                                        name="Position"
                                        label="Position"
                                        value={form.Position}
                                        onChange={handleChange}
                                        fullWidth
                                        sx={glassInputSx}
                                    >
                                        <MenuItem value="">
                                            <em>Select a position</em>
                                        </MenuItem>
                                        {positionOptions.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>

                                {/* About Me */}
                                <Box sx={{ gridColumn: { sm: "span 2" } }}>
                                    <TextField
                                        required
                                        name="AboutMe"
                                        label="Tell us about yourself"
                                        value={form.AboutMe}
                                        onChange={handleChange}
                                        multiline
                                        minRows={5}
                                        fullWidth
                                        placeholder="Share your background, experience, and why you're interested in joining Swan Solutions..."
                                        sx={glassInputSx}
                                    />
                                </Box>

                                {/* Resume Upload */}
                                <Box sx={{ gridColumn: { sm: "span 2" } }}>
                                    <FileDropzone
                                        file={form.Resume}
                                        onChange={handleChange}
                                        onClear={handleClearFile}
                                    />
                                </Box>

                                {/* Submit Button */}
                                <Box sx={{ gridColumn: { sm: "span 2" }, mt: 2 }}>
                                    <motion.div whileHover={hapticHover} whileTap={hapticTap}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            disabled={status.loading}
                                            fullWidth
                                            endIcon={
                                                status.success ? (
                                                    <CheckCircleIcon />
                                                ) : (
                                                    <SendIcon />
                                                )
                                            }
                                            sx={{
                                                py: 2,
                                                borderRadius: "16px",
                                                fontSize: "1.1rem",
                                                fontWeight: 700,
                                                textTransform: "none",
                                                background: status.success
                                                    ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.light})`
                                                    : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.dark})`,
                                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.35)}`,
                                                "&:hover": {
                                                    background: status.success
                                                        ? `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`
                                                        : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.main})`,
                                                    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.45)}`,
                                                },
                                                "&:disabled": {
                                                    background: alpha(theme.palette.primary.main, 0.5),
                                                },
                                            }}
                                        >
                                            {status.loading
                                                ? "Submitting..."
                                                : status.success
                                                    ? "Application Sent!"
                                                    : "Submit Application"}
                                        </Button>
                                    </motion.div>
                                </Box>
                            </Box>
                        </form>

                        {/* Status Messages */}
                        <AnimatePresence>
                            {(status.success || status.error) && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Box sx={{ mt: 3 }}>
                                        {status.success && (
                                            <Alert
                                                severity="success"
                                                sx={{
                                                    borderRadius: "12px",
                                                    ...glassCard(theme, 0.9),
                                                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                                }}
                                            >
                                                {status.success}
                                            </Alert>
                                        )}
                                        {status.error && (
                                            <Alert
                                                severity="error"
                                                sx={{
                                                    borderRadius: "12px",
                                                    ...glassCard(theme, 0.9),
                                                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                                }}
                                            >
                                                {status.error}
                                            </Alert>
                                        )}
                                    </Box>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Card>
                </motion.div>
            </Container>
        </Box>
    );
}